import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { apiErrorMessage } from "@/api/client";
import { KeyRound, ArrowRight, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      navigate(res.admin.must_change_password ? "/admin/ajustes" : "/admin");
    } catch (err) {
      setError(apiErrorMessage(err, "No fue posible iniciar sesión."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(800px 420px at 90% -10%, rgba(244,202,84,0.10) 0%, rgba(244,202,84,0) 60%), radial-gradient(800px 420px at 5% 100%, rgba(37,99,235,0.14) 0%, rgba(37,99,235,0) 60%)" }}
      />
      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="bg-white rounded-2xl px-4 py-2.5 shadow-xl mb-4" data-testid="admin-login-logo">
            <img src="/brand/inside-logo.png" alt="INSIDE Training & Learning" className="h-8 w-auto" />
          </div>
          <Link to="/" className="text-xs font-bold text-slate-400 hover:text-brand-gold transition-colors">&larr; Volver al inicio</Link>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_24px_rgba(15,23,42,0.06)] p-8">
          <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center mb-5">
            <KeyRound className="w-5 h-5 text-brand-slate" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-1 tracking-tight">Administrador INSIDE</h1>
          <p className="text-xs text-slate-500 mb-6 leading-relaxed">Acceso exclusivo para el equipo INSIDE. Panel de matrícula, seguimiento y resultados.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">Correo</label>
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-4 rounded-lg border border-slate-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-accent/20 focus:border-brand-accent bg-slate-50 text-sm font-medium transition-shadow"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">Contraseña</label>
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-4 rounded-lg border border-slate-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-accent/20 focus:border-brand-accent bg-slate-50 text-sm font-medium transition-shadow"
              />
            </div>
            {error && (
              <div data-testid="admin-login-error" className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{error}</div>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-11 px-6 bg-brand-slate hover:bg-slate-900 text-white font-bold rounded-xl shadow-md flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:pointer-events-none active:translate-y-[1px]"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Ingresar <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
